// Features: M004-F0778
// Tests: T-M004-0385
// WebUI #250 (room-controls-relay 3.0.0, Core #531). Pure functions only, no React.
// Inputs: `describe` is the DescribeResult from GET /v1/rooms/:id/controls. A set answer is either the SetResult (200, `result`)
// or the thrown Axios-style error (`cause`). Outcome texts follow design M004-F0778 section 4 verbatim.

import { classifyRelayOutcome } from '../../services/relayOutcome';

export const ROOM_STATE_ORDER = Object.freeze(['setup', 'in_use', 'closing_down', 'off']);
export const ROOM_STATE_LABELS = Object.freeze({
  setup: 'Setup',
  in_use: 'In Use',
  closing_down: 'Closing Down',
  off: 'Not used',
});

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isNumber = (n) => typeof n === 'number' && Number.isFinite(n);

// Room state: only the values the room supports, in contract order.
export function stateOptions(describe) {
  const roomState = describe?.room_state;
  if (!roomState) return [];
  const supported = Array.isArray(roomState.supported) ? roomState.supported : [];
  return ROOM_STATE_ORDER
    .filter((value) => supported.includes(value))
    .map((value) => ({ value, label: ROOM_STATE_LABELS[value] }));
}

export function formatTemperature(n) {
  if (!isNumber(n)) return 'Not reported';
  return `${n.toFixed(1)} °C`;
}

// Target bounds as describe returns them, or null when there is no target control.
export function targetBounds(describe) {
  const target = describe?.target_temperature;
  if (!target) return null;
  return { min: target.min, max: target.max, step: target.step };
}

// The UI checks the range before sending; the server stays the authority (409 ROOM_CONTROL_VALUE_OUT_OF_RANGE).
export function validateTarget(value, describe) {
  const bounds = targetBounds(describe);
  if (!bounds) return null;
  const message = `Choose a temperature between ${bounds.min} and ${bounds.max} °C.`;
  if (!isNumber(value)) return message;
  if (isNumber(bounds.min) && value < bounds.min) return message;
  if (isNumber(bounds.max) && value > bounds.max) return message;
  return null;
}

// ---- Error helpers (Core's §1 envelopes: { success:false, error:<code>, details:{outcome, audit_id} }) ----

const bodyOf = (cause) => {
  const data = cause?.response?.data;
  return data && typeof data === 'object' ? data : {};
};
const codeOf = (cause) => {
  const error = bodyOf(cause).error;
  if (typeof error === 'string') return error;
  return (error && typeof error.code === 'string' && error.code) || '';
};
const statusOf = (cause) => Number(cause?.response?.status);

// ---- Refusal reasons (design section 4). The server `message` is never shown. ----

export const REFUSAL_TEXT = Object.freeze({
  ROOM_CONTROL_SCOPE_INVALID: "You can't control this room.",
  FORBIDDEN: "You can't control this room.",
  // Core #531 rights refusals (nothing relayed): the same per-room rule as the climate routes.
  ROOM_ORG_MISMATCH: "You can't control this room.",
  ROOM_NOT_ASSIGNED: "You can't control this room.",
  NO_ACTIVE_BOOKING: 'You can control this room only during your booking.',
  // Core #531 link refusals (nothing relayed).
  ROOM_NOT_LINKED: "This room isn't set up for remote control.",
  ROOM_LINK_AMBIGUOUS: "This room's controls are set up ambiguously — ask an admin to check the room setup.",
  ROOM_CONTROL_ROOM_NOT_MAPPED: "This room isn't set up for remote control.",
  ROOM_CONTROL_AMBIGUOUS: "This room's controls are set up ambiguously — ask an admin to check the room setup.",
  ROOM_CONTROL_ENTITY_UNAVAILABLE: "The room's controller is unavailable right now.",
  ROOM_CONTROL_VALUE_NOT_PUBLISHED: "The room doesn't offer that state right now.",
  ROOM_CONTROL_VALUE_OUT_OF_RANGE: 'That temperature is outside the room\'s range.',
  ROOM_CONTROL_SERVICE_NOT_ALLOWLISTED: "That change isn't allowed for this room.",
  ROOM_CONTROL_IDEMPOTENCY_CONFLICT: 'A different change is already recorded for this request — refresh and try again.',
});

const FALLBACK_REFUSAL = 'The room refused this change.';

export function refusalText(code) {
  return typeof code === 'string' && hasOwn(REFUSAL_TEXT, code) ? REFUSAL_TEXT[code] : FALLBACK_REFUSAL;
}

// ---- Panel visibility for a describe answer ----
// result: { ok: true, data } | { ok: false, cause }.
export function describeVisibility(result) {
  if (result?.ok) return { kind: 'panel' };
  const cause = result?.cause;
  const status = statusOf(cause);
  const code = codeOf(cause);

  if (status === 409 && (code === 'ROOM_CONTROL_AMBIGUOUS' || code === 'ROOM_CONTROL_ENTITY_UNAVAILABLE' || code === 'ROOM_LINK_AMBIGUOUS')) {
    return { kind: 'reason', reason: refusalText(code) };
  }
  if (status === 403 || status === 404 || (status === 409 && (code === 'ROOM_CONTROL_ROOM_NOT_MAPPED' || code === 'ROOM_NOT_LINKED'))) {
    return { kind: 'hidden' };
  }
  // No response, 502, 503, 504 and *TIMEOUT: the room exists but cannot be read right now.
  if (classifyRelayOutcome(cause) === 'unknown') return { kind: 'unavailable' };
  return { kind: 'hidden' };
}

// ---- Outcome of a set (design section 4) ----

const UNKNOWN_TEXT = 'Outcome unknown — check the room.';
const INVALID_TEXT = 'The room did not accept that request.';

// Observed values as the text names them: the state label and/or "target <n> °C", joined with ", ".
function describeObserved(observed, labels) {
  const parts = [];
  if (observed?.room_state != null) parts.push(labels[observed.room_state] ?? String(observed.room_state));
  if (observed?.target_temperature != null) parts.push(`target ${observed.target_temperature} °C`);
  return parts.join(', ');
}

// control: 'room_state' | 'target_temperature'. result: the SetResult of a 200 answer. cause: the error otherwise.
// value: the requested value, used only when a confirmed answer omits the observed value.
export function setOutcomeView({ control, result, cause, labels = ROOM_STATE_LABELS, value } = {}) {
  if (!cause) {
    const outcome = result?.outcome;
    const observed = result?.observed;
    if (outcome === 'confirmed') {
      const observedValue = observed?.[control] ?? value;
      const text = control === 'room_state'
        ? `Room is now ${labels[observedValue] ?? observedValue}.`
        : `Target is now ${observedValue} °C.`;
      return { kind: 'confirmed', text, role: 'status', keepKey: false };
    }
    if (outcome === 'mismatch') {
      const named = describeObserved(observed, labels);
      const text = named ? `Didn't take — the room is now ${named}.` : "Didn't take — the room did not report a value.";
      return { kind: 'mismatch', text, role: 'status', keepKey: false };
    }
    // 'unknown', or any 200 outcome this page does not recognise: never reported as a failure.
    return { kind: 'unknown', text: UNKNOWN_TEXT, role: 'status', keepKey: true };
  }

  if (classifyRelayOutcome(cause) === 'unknown') {
    return { kind: 'unknown', text: UNKNOWN_TEXT, role: 'status', keepKey: true };
  }
  const status = statusOf(cause);
  // 403 / 404 / 409 are definite: a relayed refusal carries details.outcome 'refused', and Core's own 409s (ROOM_NOT_LINKED,
  // ROOM_LINK_AMBIGUOUS) and 403s (FORBIDDEN, ROOM_ORG_MISMATCH, ROOM_NOT_ASSIGNED, NO_ACTIVE_BOOKING) are answered before any relay.
  if (status === 403 || status === 404 || status === 409) {
    return { kind: 'refused', text: refusalText(codeOf(cause)), role: 'alert', keepKey: false };
  }
  if (status === 400) {
    return { kind: 'invalid', text: INVALID_TEXT, role: 'alert', keepKey: false };
  }
  // Any other error status is not a known refusal: the relay may have acted, so the key is kept.
  return { kind: 'unknown', text: UNKNOWN_TEXT, role: 'status', keepKey: true };
}

// ---- Idempotency-Key per attempt, held per control (design section 4, M004-F0775 section 1.2) ----
// The fingerprint is the value. Same control and value as a kept attempt reuses its key; another value makes a new key.
export function createAttemptStore(newKey) {
  const attempts = new Map();
  return {
    keyFor(control, value) {
      const attempt = attempts.get(control);
      if (attempt && attempt.value === value) return attempt.key;
      const key = newKey();
      attempts.set(control, { value, key });
      return key;
    },
    settle(control, keepKey) {
      if (!keepKey) attempts.delete(control);
    },
  };
}

// The body of a state or target set: only the control and its value. Never coupled to the other control.
export function setBody(control, value) {
  return { control, value };
}
